import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Button, Modal, Spinner } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  addPhysicalRoomBlock,
  getPhysicalRoomsByHotel,
  removePhysicalRoomBlocksInRange,
} from "../../../FIREBASECONFIG";
import {
  fetchHotels,
  fetchHotelRooms,
  fetchReservations,
} from "../../../api/resort-booking/resort.service";
import { getUserRole } from "../../../APIS";
import {
  PHYSICAL_ROOM_STATUSES,
  buildOccupiedByDate,
  isDateInBlockRange,
} from "./physicalRoomAvailability";

import "react-toastify/dist/ReactToastify.css";

export { PHYSICAL_ROOM_STATUSES };

const STATUS_META = {
  available: { label: "Available", bg: "#d1e7dd", color: "#0f5132" },
  occupied: { label: "Booked", bg: "#ffe5cc", color: "#b35c00" },
  blocked: { label: "Blocked", bg: "#f8d7da", color: "#842029" },
};

const ROOM_COL_W = 200;
const DAY_COL_W = 96;
const BORDER = "1px solid #dee2e6";

const cellBase = {
  border: BORDER,
  padding: "6px 8px",
  fontSize: 12,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  textAlign: "center",
};

const stickyLabel = (background, zIndex = 3) => ({
  ...cellBase,
  position: "sticky",
  left: 0,
  background,
  zIndex,
  textAlign: "left",
  minWidth: ROOM_COL_W,
  maxWidth: ROOM_COL_W,
  width: ROOM_COL_W,
  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.12)",
  whiteSpace: "normal",
});

const buildDays = (start, end) => {
  const s = moment(start, "YYYY-MM-DD", true);
  const e = moment(end, "YYYY-MM-DD", true);
  if (!s.isValid() || !e.isValid() || e.isBefore(s, "day")) return [];
  const days = [];
  const cursor = s.clone();
  while (cursor.isSameOrBefore(e, "day")) {
    days.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }
  return days;
};

const findBlockCoveringDate = (unit, date) =>
  (unit.blocks || []).find((block) => isDateInBlockRange(date, block)) || null;

/**
 * Cell status for a night:
 * - blocked if unit is globally blocked or has a date-range block
 * - occupied if a reservation covers that night with this unit assigned
 * - otherwise available
 */
const resolveDayStatus = (unit, date, occupiedByDate) => {
  if (unit.status === PHYSICAL_ROOM_STATUSES.blocked) {
    return PHYSICAL_ROOM_STATUSES.blocked;
  }
  if ((unit.blocks || []).some((block) => isDateInBlockRange(date, block))) {
    return PHYSICAL_ROOM_STATUSES.blocked;
  }
  if (occupiedByDate.get(date)?.has(unit.id)) {
    return PHYSICAL_ROOM_STATUSES.occupied;
  }
  return PHYSICAL_ROOM_STATUSES.available;
};

const PhysicalRooms = () => {
  const role = (getUserRole() || "").toLowerCase().replace(/\s+/g, "");
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");

  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(13, "days").format("YYYY-MM-DD")
  );
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [unitsByRoomType, setUnitsByRoomType] = useState({});
  const [occupiedByDate, setOccupiedByDate] = useState(() => new Map());

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [modalMode, setModalMode] = useState("block");
  const [blockHotelRoomId, setBlockHotelRoomId] = useState("");
  const [blockRoomId, setBlockRoomId] = useState("");
  const [blockStart, setBlockStart] = useState(moment().format("YYYY-MM-DD"));
  const [blockEnd, setBlockEnd] = useState(moment().format("YYYY-MM-DD"));
  const [blockRemarks, setBlockRemarks] = useState("");
  const [savingBlock, setSavingBlock] = useState(false);

  const days = useMemo(
    () => buildDays(startDate, endDate),
    [startDate, endDate]
  );

  const allUnits = useMemo(() => {
    const list = [];
    roomTypes.forEach((type) => {
      (unitsByRoomType[type.id] || []).forEach((unit) => {
        list.push({
          ...unit,
          hotelRoomId: type.id,
          roomTypeName: type.name || "Untitled room type",
        });
      });
    });
    return list;
  }, [roomTypes, unitsByRoomType]);

  const roomTypesWithUnits = useMemo(
    () =>
      roomTypes.filter((type) => (unitsByRoomType[type.id] || []).length > 0),
    [roomTypes, unitsByRoomType]
  );

  const physicalRoomsForBlock = useMemo(() => {
    if (!blockHotelRoomId) return [];
    return unitsByRoomType[blockHotelRoomId] || [];
  }, [blockHotelRoomId, unitsByRoomType]);

  useEffect(() => {
    const loadHotels = async () => {
      setLoadingHotels(true);
      try {
        let list = (await fetchHotels()) || [];
        if (role === "hoteladmin" && userDetails?.hotelId) {
          list = list.filter((h) => h.id === userDetails.hotelId);
        }
        list = [...list].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
        setHotels(list);
        if (list.length) {
          setHotelId((prev) => prev || list[0].id);
        }
      } catch (err) {
        console.error("Failed to load hotels:", err);
        setHotels([]);
      } finally {
        setLoadingHotels(false);
      }
    };
    loadHotels();
  }, [role, userDetails?.hotelId]);

  const loadChart = useCallback(async () => {
    if (!hotelId || !days.length) {
      setRoomTypes([]);
      setUnitsByRoomType({});
      setOccupiedByDate(new Map());
      return;
    }

    setLoadingChart(true);
    try {
      const [allRooms, physicalUnits, allReservations] = await Promise.all([
        fetchHotelRooms(),
        getPhysicalRoomsByHotel(hotelId),
        fetchReservations(),
      ]);

      const types = (allRooms || [])
        .filter((r) => String(r.hotel_id || "") === String(hotelId))
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );

      const byType = {};
      for (const type of types) {
        byType[type.id] = physicalUnits
          .filter((u) => String(u.hotel_room_id || "") === String(type.id))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      }

      const occupancy = buildOccupiedByDate(allReservations, {
        hotelId,
        nights: days,
      });

      setRoomTypes(types);
      setUnitsByRoomType(byType);
      setOccupiedByDate(occupancy);
    } catch (err) {
      console.error("Failed to load physical room chart:", err);
      setRoomTypes([]);
      setUnitsByRoomType({});
      setOccupiedByDate(new Map());
    } finally {
      setLoadingChart(false);
    }
  }, [hotelId, days]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  const openBlockModal = (unit = null, day = null, mode = "block") => {
    const defaultTypeId =
      unit?.hotel_room_id ||
      unit?.hotelRoomId ||
      roomTypesWithUnits[0]?.id ||
      "";
    const unitsForType = defaultTypeId
      ? unitsByRoomType[defaultTypeId] || []
      : [];
    const defaultUnitId = unit?.id || unitsForType[0]?.id || "";
    const covering = unit && day ? findBlockCoveringDate(unit, day) : null;

    setModalMode(mode);
    setBlockHotelRoomId(String(defaultTypeId || ""));
    setBlockRoomId(String(defaultUnitId || ""));
    setBlockStart(
      covering?.startDate || day || startDate || moment().format("YYYY-MM-DD")
    );
    setBlockEnd(
      covering?.endDate || day || startDate || moment().format("YYYY-MM-DD")
    );
    setBlockRemarks(covering?.remarks || "");
    setShowBlockModal(true);
  };

  const openCellModal = (unit, day, status) => {
    if (status === PHYSICAL_ROOM_STATUSES.occupied) return;
    if (status === PHYSICAL_ROOM_STATUSES.blocked) {
      openBlockModal(unit, day, "unblock");
      return;
    }
    openBlockModal(unit, day, "block");
  };

  const closeBlockModal = () => {
    if (savingBlock) return;
    setShowBlockModal(false);
  };

  const handleSaveBlock = async () => {
    if (!blockHotelRoomId) {
      toast.error("Please select a hotel room.");
      return;
    }
    if (!blockRoomId) {
      toast.error("Please select a physical room.");
      return;
    }
    const start = moment(blockStart, "YYYY-MM-DD", true);
    const end = moment(blockEnd, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      toast.error("Please select a valid date range.");
      return;
    }
    if (end.isBefore(start, "day")) {
      toast.error("End date must be on or after the start date.");
      return;
    }

    // Warn if any night in the range is already booked
    const bookedDays = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day")) {
      const key = cursor.format("YYYY-MM-DD");
      if (occupiedByDate.get(key)?.has(blockRoomId)) {
        bookedDays.push(key);
      }
      cursor.add(1, "day");
    }
    if (bookedDays.length) {
      toast.error(
        "This room is already booked on one or more selected dates. Choose another range."
      );
      return;
    }

    setSavingBlock(true);
    try {
      await addPhysicalRoomBlock(blockRoomId, {
        startDate: blockStart,
        endDate: blockEnd,
        remarks: blockRemarks,
      });
      toast.success("Room blocked for the selected dates.");
      setShowBlockModal(false);
      await loadChart();
    } catch (err) {
      console.error("Failed to block room:", err);
      toast.error("Failed to block room. Please try again.");
    } finally {
      setSavingBlock(false);
    }
  };

  const selectedUnit = useMemo(
    () => allUnits.find((u) => String(u.id) === String(blockRoomId)),
    [allUnits, blockRoomId]
  );

  const handleSaveUnblock = async () => {
    if (!blockHotelRoomId) {
      toast.error("Please select a hotel room.");
      return;
    }
    if (!blockRoomId) {
      toast.error("Please select a physical room.");
      return;
    }
    const start = moment(blockStart, "YYYY-MM-DD", true);
    const end = moment(blockEnd, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid()) {
      toast.error("Please select a valid date range.");
      return;
    }
    if (end.isBefore(start, "day")) {
      toast.error("End date must be on or after the start date.");
      return;
    }

    const hasDateBlocks = (selectedUnit?.blocks || []).some((block) => {
      const bStart = moment(block.startDate, "YYYY-MM-DD", true);
      const bEnd = moment(block.endDate, "YYYY-MM-DD", true);
      if (!bStart.isValid() || !bEnd.isValid()) return false;
      return start.isSameOrBefore(bEnd, "day") && end.isSameOrAfter(bStart, "day");
    });
    const isGloballyBlocked =
      selectedUnit?.status === PHYSICAL_ROOM_STATUSES.blocked;

    if (!hasDateBlocks && !isGloballyBlocked) {
      toast.error("This room is not blocked on the selected dates.");
      return;
    }

    setSavingBlock(true);
    try {
      await removePhysicalRoomBlocksInRange(blockRoomId, {
        startDate: blockStart,
        endDate: blockEnd,
      });
      toast.success("Room set available for the selected dates.");
      setShowBlockModal(false);
      await loadChart();
    } catch (err) {
      console.error("Failed to unblock room:", err);
      toast.error("Failed to unblock room. Please try again.");
    } finally {
      setSavingBlock(false);
    }
  };

  const hasRows = roomTypes.some(
    (type) => (unitsByRoomType[type.id] || []).length > 0
  );

  return (
    <div className="col-12">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h4 className="card-title m-0">Physical Rooms</h4>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <div
                key={key}
                className="d-flex align-items-center gap-2"
                style={{ fontSize: 13 }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: meta.bg,
                    border: `1px solid ${meta.color}`,
                    display: "inline-block",
                  }}
                />
                {meta.label}
              </div>
            ))}
            <Button
              variant="danger"
              size="sm"
              disabled={!allUnits.length}
              onClick={() => openBlockModal(null, null, "block")}
            >
              Block Room
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              disabled={!allUnits.length}
              onClick={() => openBlockModal(null, null, "unblock")}
            >
              Unblock Room
            </Button>
          </div>
        </div>

        <div className="card-body">
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Resort / Hotel</label>
              <select
                className="form-select"
                value={hotelId}
                disabled={loadingHotels || !hotels.length}
                onChange={(e) => setHotelId(e.target.value)}
              >
                {!hotels.length && <option value="">No hotels found</option>}
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name || hotel.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  const next = e.target.value;
                  setStartDate(next);
                  if (moment(endDate).isBefore(moment(next), "day")) {
                    setEndDate(next);
                  }
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div style={{ position: "relative", minHeight: 180 }}>
            {(loadingHotels || loadingChart) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.8)",
                  zIndex: 20,
                }}
              >
                <div className="text-center">
                  <FaSpinner
                    className="spin"
                    style={{ fontSize: 26, marginBottom: 8 }}
                  />
                  <div>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading chart…
                  </div>
                </div>
              </div>
            )}

            {!days.length ? (
              <p className="text-muted mb-0">
                Select a valid date range to view the chart.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    borderCollapse: "collapse",
                    minWidth: ROOM_COL_W + days.length * DAY_COL_W,
                    width: "100%",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          ...stickyLabel("#f8f9fa", 5),
                          fontWeight: 600,
                          top: 0,
                          position: "sticky",
                        }}
                      >
                        Room
                      </th>
                      {days.map((day) => (
                        <th
                          key={day}
                          style={{
                            ...cellBase,
                            background: "#f8f9fa",
                            fontWeight: 600,
                            minWidth: DAY_COL_W,
                            width: DAY_COL_W,
                            position: "sticky",
                            top: 0,
                            zIndex: 4,
                          }}
                        >
                          <div>{moment(day).format("ddd")}</div>
                          <div>{moment(day).format("DD MMM")}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!hasRows ? (
                      <tr>
                        <td
                          colSpan={days.length + 1}
                          style={{
                            ...cellBase,
                            textAlign: "center",
                            color: "#6c757d",
                            padding: 24,
                          }}
                        >
                          No physical rooms found for this hotel. Add them under
                          Hotels → Rooms first.
                        </td>
                      </tr>
                    ) : (
                      roomTypes.map((type) => {
                        const units = unitsByRoomType[type.id] || [];
                        if (!units.length) return null;
                        return (
                          <React.Fragment key={type.id}>
                            <tr>
                              <td
                                style={{
                                  ...stickyLabel("#eef1f5"),
                                  fontWeight: 700,
                                }}
                              >
                                {type.name || "Untitled room type"}
                              </td>
                              {days.map((day) => (
                                <td
                                  key={`${type.id}-${day}-hdr`}
                                  style={{
                                    ...cellBase,
                                    background: "#eef1f5",
                                  }}
                                />
                              ))}
                            </tr>
                            {units.map((unit) => (
                              <tr key={unit.id}>
                                <td style={stickyLabel("#ffffff")}>
                                  {unit.label || unit.id}
                                </td>
                                {days.map((day) => {
                                  const status = resolveDayStatus(
                                    unit,
                                    day,
                                    occupiedByDate
                                  );
                                  const meta =
                                    STATUS_META[status] || STATUS_META.available;
                                  const isBooked =
                                    status === PHYSICAL_ROOM_STATUSES.occupied;
                                  const hint = isBooked
                                    ? "booked"
                                    : status === PHYSICAL_ROOM_STATUSES.blocked
                                    ? "click to unblock"
                                    : "click to block";
                                  return (
                                    <td
                                      key={`${unit.id}-${day}`}
                                      title={`${unit.label || unit.id} · ${moment(
                                        day
                                      ).format(
                                        "DD MMM YYYY"
                                      )} · ${meta.label} (${hint})`}
                                      onClick={() =>
                                        openCellModal(unit, day, status)
                                      }
                                      style={{
                                        ...cellBase,
                                        background: meta.bg,
                                        color: meta.color,
                                        fontWeight: 600,
                                        minWidth: DAY_COL_W,
                                        width: DAY_COL_W,
                                        cursor: isBooked ? "default" : "pointer",
                                      }}
                                    >
                                      {meta.label}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showBlockModal} onHide={closeBlockModal} centered>
        <Modal.Header closeButton={!savingBlock}>
          <Modal.Title>
            {modalMode === "unblock" ? "Unblock Room" : "Block Room"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMode === "unblock" &&
            selectedUnit?.status === PHYSICAL_ROOM_STATUSES.blocked && (
              <p className="text-muted small">
                This room is blocked for all dates. Unblocking will set it
                available again.
              </p>
            )}
          <div className="mb-3">
            <label className="form-label">Hotel Room</label>
            <select
              className="form-select"
              value={blockHotelRoomId}
              disabled={savingBlock}
              onChange={(e) => {
                const nextTypeId = e.target.value;
                setBlockHotelRoomId(nextTypeId);
                const firstUnit = (unitsByRoomType[nextTypeId] || [])[0];
                setBlockRoomId(firstUnit?.id || "");
              }}
            >
              <option value="">Select hotel room</option>
              {roomTypesWithUnits.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name || "Untitled room type"}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Physical Room</label>
            <select
              className="form-select"
              value={blockRoomId}
              onChange={(e) => setBlockRoomId(e.target.value)}
              disabled={savingBlock || !blockHotelRoomId}
            >
              <option value="">Select physical room</option>
              {physicalRoomsForBlock.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.label || unit.id}
                </option>
              ))}
            </select>
          </div>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-control"
                value={blockStart}
                disabled={savingBlock}
                onChange={(e) => {
                  const next = e.target.value;
                  setBlockStart(next);
                  if (moment(blockEnd).isBefore(moment(next), "day")) {
                    setBlockEnd(next);
                  }
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-control"
                value={blockEnd}
                min={blockStart}
                disabled={savingBlock}
                onChange={(e) => setBlockEnd(e.target.value)}
              />
            </div>
          </div>
          {modalMode === "block" && (
            <div className="mb-0">
              <label className="form-label">Remarks (optional)</label>
              <textarea
                className="form-control"
                rows={2}
                value={blockRemarks}
                disabled={savingBlock}
                placeholder="Reason for blocking…"
                onChange={(e) => setBlockRemarks(e.target.value)}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeBlockModal}
            disabled={savingBlock}
          >
            Cancel
          </Button>
          {modalMode === "unblock" ? (
            <Button
              variant="success"
              onClick={handleSaveUnblock}
              disabled={savingBlock || !blockRoomId}
            >
              {savingBlock ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                "Set Room Available"
              )}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={handleSaveBlock}
              disabled={savingBlock || !blockRoomId}
            >
              {savingBlock ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                "Block Room"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PhysicalRooms;
